import React from "react";
import { connect } from "react-redux";

import inits from "../../engine/inits";
import RenderCharacters from "../../engine/renderCharacters";

import { fetchUrl } from "../../config/utils";
import {
    clases,
    razas,
    generos,
    nameClases,
    nameGeneros,
    nameRazas
} from "../../config/config";

import style from "./style.scss";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faAngleLeft,
    faAngleRight
} from "@fortawesome/free-solid-svg-icons";

// Mapeo completo raza × género → idBody y rango de heads válidos.
// Body IDs sacados de la función `bodyNaked` del SERVER (game.js:1312-1362).
// Head ranges scaneando inits/heads.json: rangos donde TODOS los IDs tienen un
// grh válido (sin gaps).
const RACE_DATA = {
    [razas.humano]: {
        [generos.hombre]: { body: 21, head: [1, 40] },
        [generos.mujer]: { body: 39, head: [70, 89] }
    },
    [razas.elfo]: {
        [generos.hombre]: { body: 210, head: [101, 122] },
        [generos.mujer]: { body: 259, head: [170, 188] }
    },
    [razas.elfoDrow]: {
        [generos.hombre]: { body: 32, head: [201, 221] },
        [generos.mujer]: { body: 40, head: [270, 288] }
    },
    [razas.enano]: {
        [generos.hombre]: { body: 53, head: [301, 319] },
        [generos.mujer]: { body: 60, head: [370, 385] }
    },
    [razas.gnomo]: {
        [generos.hombre]: { body: 222, head: [401, 418] },
        [generos.mujer]: { body: 260, head: [470, 486] }
    }
};

const getRaceGenderData = (raza, genero) =>
    (RACE_DATA[raza] && RACE_DATA[raza][genero]) ||
    RACE_DATA[razas.humano][generos.hombre];

// Clase 5 (ladrón) está deshabilitada en el original — la saltamos.
const CLASES_VALIDAS = Object.values(clases).filter(id => id !== 5 && id <= 9);

// Form de creación de personaje — componente reusable. Vive embebido dentro
// de /play debajo del grid del lobby. NO hace navegación post-create — llama
// a `onCreated(character)` para que el padre maneje el flujo.
class CreateCharacter extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            name: "",
            nameError: "",
            submitting: false,
            idClaseSelected: 1,
            idRazaSelected: 1,
            idGeneroSelected: 1,
            idHeadSelected: 1,
            character: {
                idBody: 1,
                idHead: 1,
                idWeapon: 48,
                idShield: 0,
                idHelmet: 0,
                idGenero: 1
            }
        };

        this.canvasRef = null;
        this.ctx = null;
    }

    componentDidMount() {
        if (this.canvasRef) {
            this.ctx = this.canvasRef.getContext("2d");
        }
        if (this.props.initsLoaded) this.drawChar();
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevState.character !== this.state.character ||
            (!prevProps.initsLoaded && this.props.initsLoaded)
        ) {
            this.drawChar();
        }
    }

    drawChar = () => {
        if (!this.props.initsLoaded || !this.ctx) return;
        this.ctx.clearRect(0, 0, 80, 100);
        const rndChar = new RenderCharacters(
            inits,
            this.ctx,
            this.state.character,
            24,
            60
        );
        rndChar.drawChar();
    };

    // ── Head ──────────────────────────────────────────────────
    prevHead = () => {
        const { idRazaSelected, idGeneroSelected, idHeadSelected, character } =
            this.state;
        const data = getRaceGenderData(idRazaSelected, idGeneroSelected);
        const [first] = data.head;
        if (idHeadSelected <= first) return;
        const next = idHeadSelected - 1;
        this.setState({
            idHeadSelected: next,
            character: { ...character, idHead: next }
        });
    };

    nextHead = () => {
        const { idRazaSelected, idGeneroSelected, idHeadSelected, character } =
            this.state;
        const data = getRaceGenderData(idRazaSelected, idGeneroSelected);
        const [, last] = data.head;
        if (idHeadSelected >= last) return;
        const next = idHeadSelected + 1;
        this.setState({
            idHeadSelected: next,
            character: { ...character, idHead: next }
        });
    };

    // ── Clase ─────────────────────────────────────────────────
    cycleClase = dir => {
        const { idClaseSelected, character } = this.state;
        const idx = CLASES_VALIDAS.indexOf(idClaseSelected);
        if (idx === -1) return;
        const nextIdx = idx + dir;
        if (nextIdx < 0 || nextIdx >= CLASES_VALIDAS.length) return;
        const nextClase = CLASES_VALIDAS[nextIdx];
        const idWeapon = nextClase === clases.cazador ? 40 : 48;
        this.setState({
            idClaseSelected: nextClase,
            character: { ...character, idWeapon }
        });
    };

    // ── Raza ──────────────────────────────────────────────────
    cycleRaza = dir => {
        const { idRazaSelected, idGeneroSelected, character } = this.state;
        const next = idRazaSelected + dir;
        if (next < 1 || next > 5) return;
        const data = getRaceGenderData(next, idGeneroSelected);
        const [firstHead] = data.head;
        this.setState({
            idRazaSelected: next,
            idHeadSelected: firstHead,
            character: {
                ...character,
                idHead: firstHead,
                idBody: data.body
            }
        });
    };

    // ── Género ────────────────────────────────────────────────
    cycleGenero = dir => {
        const { idGeneroSelected, idRazaSelected, character } = this.state;
        const next = idGeneroSelected + dir;
        if (next < 1 || next > 2) return;
        const data = getRaceGenderData(idRazaSelected, next);
        const [firstHead] = data.head;
        this.setState({
            idGeneroSelected: next,
            idHeadSelected: firstHead,
            character: {
                ...character,
                idGenero: next,
                idHead: firstHead,
                idBody: data.body
            }
        });
    };

    onNameChange = e => {
        const name = e.target.value;
        let nameError = "";
        if (name && name.trim().length > 0) {
            if (name.trim().length < 3) nameError = "Mínimo 3 caracteres";
            else if (name.length > 20) nameError = "Máximo 20 caracteres";
            else if (!/^[a-zA-Z\s]+$/.test(name))
                nameError = "Solo letras y espacios";
        }
        this.setState({ name, nameError });
    };

    createCharacter = async () => {
        const {
            name,
            nameError,
            character,
            idClaseSelected,
            idRazaSelected,
            submitting
        } = this.state;

        if (submitting) return;
        if (!name || name.trim().length < 3) {
            this.setState({ nameError: "Mínimo 3 caracteres" });
            return;
        }
        if (nameError) return;

        this.setState({ submitting: true });

        try {
            const result = await fetchUrl("/character", {
                method: "POST",
                body: JSON.stringify({
                    name: name.trim(),
                    idClase: idClaseSelected,
                    idRaza: idRazaSelected,
                    idGenero: character.idGenero,
                    idHead: character.idHead,
                    idBody: character.idBody,
                    idWeapon: character.idWeapon,
                    idShield: character.idShield,
                    idHelmet: character.idHelmet
                }),
                headers: { "Content-Type": "application/json" },
                credentials: "include"
            });

            if (result.error) {
                this.setState({
                    submitting: false,
                    nameError: result.message || "Error al crear personaje"
                });
                return;
            }

            // Padre maneja el flujo post-create (refresh lobby + entrar al juego).
            if (this.props.onCreated) this.props.onCreated(result);
        } catch (err) {
            this.setState({
                submitting: false,
                nameError: "Error de red. Intentá de nuevo."
            });
        }
    };

    render() {
        const {
            name,
            nameError,
            submitting,
            idClaseSelected,
            idRazaSelected,
            idGeneroSelected
        } = this.state;

        const canSubmit =
            name.trim().length >= 3 && !nameError && !submitting;

        const Stepper = ({
            label,
            value,
            onPrev,
            onNext,
            prevDisabled,
            nextDisabled
        }) => (
            <div className={style.field}>
                <label className={style.fieldLabel}>{label}</label>
                <div className={style.stepper}>
                    <button
                        type="button"
                        className={style.stepperBtn}
                        onClick={onPrev}
                        disabled={prevDisabled}
                        aria-label={`${label} anterior`}
                    >
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </button>
                    <div className={style.stepperValue}>{value}</div>
                    <button
                        type="button"
                        className={style.stepperBtn}
                        onClick={onNext}
                        disabled={nextDisabled}
                        aria-label={`${label} siguiente`}
                    >
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
            </div>
        );

        const raceGenderData = getRaceGenderData(
            idRazaSelected,
            idGeneroSelected
        );
        const [firstHead, lastHead] = raceGenderData.head;
        const claseIdx = CLASES_VALIDAS.indexOf(idClaseSelected);

        return (
            <div className={style.root}>
                {/* Body: preview a la izquierda, form a la derecha. Layout
                    horizontal en desktop, vertical en mobile (ver style.scss). */}
                <div className={style.body}>
                    <div className={style.previewCol}>
                        <div className={style.previewFrame}>
                            <canvas
                                ref={ref => {
                                    this.canvasRef = ref;
                                }}
                                className={style.previewCanvas}
                                width="80"
                                height="100"
                            />
                            <div className={style.previewMeta}>
                                <span>Cabeza #{this.state.idHeadSelected}</span>
                                <span>Body #{this.state.character.idBody}</span>
                            </div>
                        </div>
                        <div className={style.previewArrows}>
                            <button
                                type="button"
                                className={style.previewArrow}
                                onClick={this.prevHead}
                                disabled={
                                    this.state.idHeadSelected <= firstHead
                                }
                                aria-label="Cabeza anterior"
                            >
                                <FontAwesomeIcon icon={faAngleLeft} />
                            </button>
                            <span className={style.previewArrowsLabel}>
                                Cabeza
                            </span>
                            <button
                                type="button"
                                className={style.previewArrow}
                                onClick={this.nextHead}
                                disabled={
                                    this.state.idHeadSelected >= lastHead
                                }
                                aria-label="Cabeza siguiente"
                            >
                                <FontAwesomeIcon icon={faAngleRight} />
                            </button>
                        </div>
                    </div>

                    <div className={style.formCol}>
                        <div className={style.field}>
                            <label
                                htmlFor="cc-name"
                                className={style.fieldLabel}
                            >
                                Nombre del personaje
                            </label>
                            <input
                                type="text"
                                id="cc-name"
                                className={`${style.input} ${
                                    nameError ? style.inputError : ""
                                }`}
                                value={name}
                                onChange={this.onNameChange}
                                placeholder="Ej. Aragorn"
                                maxLength={20}
                                autoComplete="off"
                            />
                            {nameError ? (
                                <span className={style.errorMsg}>
                                    {nameError}
                                </span>
                            ) : (
                                <span className={style.helpMsg}>
                                    3-20 caracteres, solo letras
                                </span>
                            )}
                        </div>

                        <div className={style.fieldsRow}>
                            <Stepper
                                label="Clase"
                                value={nameClases[idClaseSelected]}
                                onPrev={() => this.cycleClase(-1)}
                                onNext={() => this.cycleClase(1)}
                                prevDisabled={claseIdx === 0}
                                nextDisabled={
                                    claseIdx === CLASES_VALIDAS.length - 1
                                }
                            />
                            <Stepper
                                label="Raza"
                                value={nameRazas[idRazaSelected]}
                                onPrev={() => this.cycleRaza(-1)}
                                onNext={() => this.cycleRaza(1)}
                                prevDisabled={idRazaSelected <= 1}
                                nextDisabled={idRazaSelected >= 5}
                            />
                        </div>

                        <div className={style.fieldsRow}>
                            <Stepper
                                label="Género"
                                value={nameGeneros[idGeneroSelected]}
                                onPrev={() => this.cycleGenero(-1)}
                                onNext={() => this.cycleGenero(1)}
                                prevDisabled={idGeneroSelected <= 1}
                                nextDisabled={idGeneroSelected >= 2}
                            />
                            <div className={style.field}>
                                <label className={style.fieldLabel}>
                                    Ciudad inicial
                                </label>
                                <div className={style.cityDisplay}>
                                    Ullathorpe
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <button
                    type="button"
                    className={style.submitBtn}
                    onClick={this.createCharacter}
                    disabled={!canSubmit}
                >
                    {submitting ? "Creando..." : "Crear personaje"}
                </button>
            </div>
        );
    }
}

function mapStateToProps(state) {
    const { initsLoaded, account } = state;
    return { initsLoaded, account };
}

export default connect(mapStateToProps)(CreateCharacter);
